import { Field } from "components/dynamic/DynamicForm";
import DynamicFilter from "components/filter/DynamicFilter";
import MobileFilterDrawer from "components/filter/MobileFilterDrawer";
import HeaderBox from "components/navbar/HeaderBox";
import DynamicTable from "components/table/DynamicTable";
import Pagination from "components/table/Pagination";
import { useModal } from "hooks/useModal";
import { useScope } from "hooks/useScope";
import moment from "moment-jalaali";
import { useState } from "react";
import { FieldValues } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TbReportAnalytics } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { useGetOrganizationAssessmentQuery } from "reduxFiles/api/assessmentApi";
import { useLazyGetCompaniesQuery } from "reduxFiles/api/companyApi";
import { enumToOptions } from "services/helper";
import {
  AssessmentStatus,
  OrganizationAssessment,
  OrganizationAssessmentStatus,
} from "types/assessmentTypes";
import { Column, TableAction } from "types/globalTypes";
import CreateOrganizationAssessmentModal from "./CreateOrganizationAssessmentModal";

const OrganizationAssessmentPage = () => {
  // hooks
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const scope = useScope();
  const navigate = useNavigate();

  // states
  const modal = useModal();

  // filters
  const [filters, setFilters] = useState({});
  const [showFilters, setShowFilters] = useState(false);

  // pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
  });

  // apis
  const { data, isFetching, refetch } = useGetOrganizationAssessmentQuery({
    scope,
    page: pagination.page,
    limit: pagination.limit,
    filters,
  });

  // companies
  const [triggerCompanies] = useLazyGetCompaniesQuery();

  const searchCompanies = async (text: string) => {
    try {
      const res = await triggerCompanies({
        scope,
        page: 1,
        limit: 50,
        filters: { name: text },
      }).unwrap();

      return res.items.map((u) => ({
        label: u.name,
        value: u.id,
      }));
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  // form
  const fields: Field[] = [
    {
      name: "companyId",
      label: t("assessment.companyId"),
      type: "select",
      onSearch: searchCompanies,
      formed: true,
      required: true,
      filtered: true,
    },
    {
      name: "licenseKey",
      label: t("assessment.licenseKey"),
      type: "text",
      formed: true,
      required: true,
    },
    {
      name: "status",
      label: t("assessment.status"),
      type: "select",
      options: enumToOptions(AssessmentStatus, t, "assessment.statuses"),
      filtered: true,
    },
    {
      name: "decisionStatus",
      label: t("organizationAssessmentReport.decisionStatus"),
      type: "select",
      options: enumToOptions(
        OrganizationAssessmentStatus,
        t,
        "organizationAssessmentReport.statusCard.title"
      ),
      filtered: true,
    },
  ];

  const columns: Column<OrganizationAssessment>[] = [
    {
      key: "createdAt",
      title: t("table.createdAt"),
      render: (row) =>
        moment(row.createdAt).format(
          lang === "fa" ? "jYYYY/jMM/jDD HH:mm" : "YYYY/MM/DD HH:mm"
        ),
    },
    {
      key: "organization",
      title: t("common.organization"),
      render: (row) => row.organization?.name ?? "",
    },
    {
      key: "company",
      title: t("assessment.companyId"),
      render: (row) => row.company?.name ?? "",
    },
    {
      key: "status",
      title: t("assessment.status"),
      render: (row) => t(`assessment.statuses.${row.status}`),
    },
    {
      key: "assessment",
      title: t("assessment.report.datas"),
      render: (row) => (
        <div className="flex flex-col items-center justify-center">
          <span dir="ltr">
            OHI : {row.companyOhi ? row.companyOhi.toFixed(1) + " %" : "-"}
          </span>
          <span dir="ltr">
            IEII :{" "}
            {row.category
              ? `${row.finalScore?.toFixed(2) ?? ""} % (${row.category ?? ""})`
              : "-"}
          </span>
        </div>
      ),
    },
    {
      key: "decisionStatus",
      title: t("organizationAssessmentReport.decisionStatus"),
      render: (row) =>
        row.decisionStatus
          ? t(
              `organizationAssessmentReport.statusCard.title.${row.decisionStatus}`
            )
          : "",
    },
  ];

  const actions: TableAction<OrganizationAssessment>[] = [
    {
      label: t("assessment.assessmentReport"),
      onClick: (row) => {
        navigate(`/${scope}/organization-assessment/${row.id}/report`);
      },
      color: "success",
      icon: <TbReportAnalytics />,
    },
  ];

  // functions
  const handleFilterSubmit = (filterData: FieldValues) => {
    setFilters(filterData);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination({
      page: 1,
      limit: newLimit,
    });
  };

  return (
    <div>
      <div className="mt-5 h-full">
        <HeaderBox
          title={t("assessment.organizationAssessmentList")}
          createBtn={{
            title: t("organizationAssessmentReport.addOrganizationAssessment"),
            handler: () => {
              modal.openModal();
            },
          }}
          filterBtn={{
            title: t("buttons.filters"),
            handler: () => setShowFilters((prev) => !prev),
          }}
          refreshBtn={refetch}
        />

        <MobileFilterDrawer
          open={showFilters}
          onClose={() => setShowFilters((prev) => !prev)}
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          loading={isFetching}
        />
        <DynamicFilter
          onSubmit={handleFilterSubmit}
          fields={fields?.filter((f) => f.filtered)}
          defaultValues={{}}
          loading={isFetching}
        />

        <DynamicTable<OrganizationAssessment>
          columns={columns}
          data={data?.items ?? []}
          actions={actions}
          loading={isFetching}
          pagination={pagination}
        />
        <Pagination
          currentPage={pagination?.page || 1}
          totalPages={data?.pageCount || 1}
          onPageChange={(newPage) => {
            setPagination((prev) => ({ ...prev, page: newPage }));
          }}
          totalItems={data?.total || 0}
          currentLimit={pagination.limit}
          onLimitChange={handleLimitChange}
        />

        <CreateOrganizationAssessmentModal
          isOpen={modal.isOpen}
          onClose={modal.closeModal}
          modal={modal}
          refetch={refetch}
        />
      </div>
    </div>
  );
};

export default OrganizationAssessmentPage;
